import React from 'react';
import ReactDOM from 'react-dom';
import CreditPurchaseModal from '../common/CreditPurchaseModal';
import './VirtualTryOnErrorModal.css';

const VirtualTryOnErrorModal = ({
  isOpen,
  onClose,
  error,
  onRetry,
  onSwitchToManual,
  showRetry = true,
  showSwitchToManual = true
}) => {
  if (!isOpen) return null;

  const getErrorMessage = (error) => {
    if (!error) return { title: 'Unknown Error', message: 'An unknown error occurred' };

    const message = error.message || error;

    // Wearing someone else's upload needs a subscription, not more credits.
    // Matched on code where we have the error object, and on the server's
    // wording where only the message survived.
    if (
      error.code === 'purchase_required' ||
      error.code === 'subscription_required' ||
      message.includes('to try on items shared by other creators')
    ) {
      return {
        title: 'Get Credits to Wear This',
        message: 'Trying on another creator\u2019s piece needs credits you\u2019ve bought.',
        suggestion: 'Buy a pack or subscribe \u2014 either way the creator gets paid when you wear their work.',
        isSubscriptionRequired: true
      };
    }

    if (message.includes('Authentication') || message.includes('log in')) {
      return {
        title: 'Authentication Required',
        message: 'Please log in to use virtual try-on features.',
        suggestion: 'You can still browse items in your closet.'
      };
    } else if (message.includes('Insufficient credits')) {
      return {
        title: 'Out of Credits',
        message: 'You need credits for AI generation.',
        suggestion: 'Purchase credits or earn them for free.',
        isInsufficientCredits: true
      };
    } else if (message.includes('quota') || message.includes('limit')) {
      return {
        title: 'Service Temporarily Unavailable',
        message: 'Virtual try-on service has reached its usage limit.',
        suggestion: 'Please try again later or add items manually.'
      };
    } else if (message.includes('person not detected') || message.includes('Person not detected')) {
      return {
        title: 'Person Not Detected',
        message: 'Could not detect a person in your uploaded image.',
        suggestion: 'Please upload a clearer photo with a person visible, or add items manually.'
      };
    } else if (message.includes('billing') || message.includes('403')) {
      return {
        title: 'Service Configuration Issue',
        message: 'Virtual try-on service needs to be configured.',
        suggestion: 'Please contact support or add items manually for now.'
      };
    } else if (message.includes('timeout')) {
      return {
        title: 'Request Timeout',
        message: 'The virtual try-on request took too long to process.',
        suggestion: 'Please try again with smaller images or add items manually.'
      };
    } else {
      return {
        title: 'Virtual Try-On Failed',
        message: message
      };
    }
  };

  const errorInfo = getErrorMessage(error);

  // For insufficient credits, directly show the CreditPurchaseModal
  // instead of the ugly error screen
  if (errorInfo.isInsufficientCredits || errorInfo.isSubscriptionRequired) {
    return (
      <CreditPurchaseModal
        isOpen={true}
        onClose={onClose}
        reason={errorInfo.isSubscriptionRequired ? 'subscription_required' : 'insufficient_credits'}
      />
    );
  }

  const modalContent = (
    <div className="virtual-try-on-error-modal-overlay" onClick={onClose}>
      <div className="virtual-try-on-error-modal" onClick={(e) => e.stopPropagation()}>
        <div className="error-modal-header">
          <h3>{errorInfo.title}</h3>
          <button className="error-modal-close" onClick={onClose}>
            x
          </button>
        </div>

        <div className="error-modal-content">
          <div className="error-icon">!</div>
          <p className="error-message">{errorInfo.message}</p>
          {errorInfo.suggestion && (
            <p className="error-suggestion">{errorInfo.suggestion}</p>
          )}
        </div>

        <div className="error-modal-actions">
          {showRetry && onRetry && (
            <button className="error-modal-retry" onClick={onRetry}>
              Try Again
            </button>
          )}
          <button className="error-modal-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document root level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default VirtualTryOnErrorModal;